import {
	AttachmentList,
	Button,
	csvEncode,
	IconButton,
	MuiAlert,
	Popover,
	StatusChip,
	strIsNullOrEmpty,
	Tooltip,
	Typography
} from '@fox/atoms';
import { DATE_FORMAT, OverflowTextCell, ResponsiveTable } from '@fox/data-grid';
import { ResponsiveTableColumn } from '@fox/data-grid/src/responsive-table/contracts';
import { useSubscribeTopicWithHandler } from '@fox/extensions';
import { FlexRowSpaced, Padded, VerticalInset } from '@fox/layout';
import { useMediaQuery } from '@mui/material';
import moment from 'moment';
import { useState } from 'react';
import { CSVLink } from 'react-csv';
import { useContentContext } from '../../../content-context';
import { useGoalsContext } from '../../../goals-context';
import { GOALS_DATA_FETCHED } from '../../../topics';
import { ButtonLinkWrapper } from '../../action-centre/shared-components';
import { TableWrapper } from '../../financial-details/receipts';
import { renderDate } from '../../financial-details/shared';
import { useGoalsActivities } from '../../query-hooks/query-implementations';
import { ACTIVITY_STATUS, ActivityEditRule, SwiftFoxGoalActivity } from '../contracts';
import { ActivityDrawerDesktop, ActivityDrawerMobile } from './activities-drawer';
import { DeleteButtonDesktop, DeleteButtonMobile } from './delete-button';

export function GoalsActivitiesTable({
	useLinkedOrganisationData,
	allowAdd,
	defaultStatus,
	deleteRule,
	editRule,
	rowsExpandedOnSmallScreens
}: {
	useLinkedOrganisationData: boolean;
	allowAdd: boolean;
	rowsExpandedOnSmallScreens: boolean;
	defaultStatus?: string;
	deleteRule?: ActivityEditRule;
	editRule?: ActivityEditRule;
}) {
	const { contentId, requestToken } = useContentContext();
	const { getGoalPointsCategories, getGoalsConfig, loading, error } = useGoalsContext();
	const activityLabel = getGoalsConfig()?.labelOverrides?.Activity || 'Activity';
	const [page, setPage] = useState<number>(0);
	const PAGE_SIZE = 10;

	const {
		data: activityData,
		error: activityError,
		loading: activityLoading,
		refetch: activityRefetch
	} = useGoalsActivities({
		contentId,
		requestToken,
		page,
		pageSize: PAGE_SIZE,
		useLinkedOrganisationData,
		triggers: ['page']
	});

	useSubscribeTopicWithHandler(GOALS_DATA_FETCHED, () => activityRefetch());

	const isMobile = useMediaQuery('(max-width: 800px)');

	if (!strIsNullOrEmpty(activityError)) return <MuiAlert severity="error">{activityError}</MuiAlert>;
	if (!strIsNullOrEmpty(error)) return <MuiAlert severity="error">{error}</MuiAlert>;
	if (loading || activityLoading) return <></>;

	const columns: ResponsiveTableColumn<SwiftFoxGoalActivity>[] = [
		{
			title: 'Date Recorded',
			dataIndex: 'dateRecorded',
			render: record => {
				if (record.dateRecorded) {
					return <OverflowTextCell>{moment(record.dateRecorded).format(DATE_FORMAT)}</OverflowTextCell>;
				}
			}
		},
		{
			title: 'Source',
			dataIndex: 'sourceId',
			render: record => {
				const config = getGoalsConfig();
				const source = config?.activitySources?.find(c => c.id === record.sourceId);
				const subSource = source?.subSources?.find(s => s.id === record.subSourceId);
				const sourceName = source?.name || '';
				const subSourceName = subSource?.name ? ` / ${subSource?.name}` : '';
				const fullLabel = sourceName + subSourceName;
				return <OverflowTextCell>{fullLabel}</OverflowTextCell>;
			},
			showOnMobileRow: true
		},
		{
			title: 'Status',
			dataIndex: 'status',
			render: record => (
				<StatusChip maxWidth={'15rem'} center label={record.status} backgroundColor={ACTIVITY_STATUS[record.status]} />
			),
			showOnMobileRow: true
		},
		{
			title: 'Points',
			dataIndex: 'points',
			render: record => {
				const pointsTotal = record.points?.reduce((acc, cur) => acc + cur.points, 0);
				const pointCategories = getGoalPointsCategories(useLinkedOrganisationData);
				return (
					<Tooltip
						title={
							<div>
								{record?.points?.map(p => {
									const categoryName = pointCategories?.find(c => c.id === p.pointCategoryId)?.name;
									return (
										<Typography key={p.pointCategoryId} variant="body2">
											{categoryName}: {p.points}
										</Typography>
									);
								})}
							</div>
						}
					>
						<div>{pointsTotal}</div>
					</Tooltip>
				);
			}
		},
		{
			title: 'Date Occurred',
			dataIndex: 'effectiveDate',
			render: record => {
				if (record.effectiveDate) {
					return <OverflowTextCell>{moment(record.effectiveDate).format(DATE_FORMAT)}</OverflowTextCell>;
				}
			}
		},
		{
			title: 'Date Confirmed',
			dataIndex: 'dateConfirmed',

			render: record => {
				if (record.dateConfirmed) {
					return <OverflowTextCell>{moment(record.dateConfirmed).format(DATE_FORMAT)}</OverflowTextCell>;
				}
			}
		},
		{
			title: 'Documents',
			dataIndex: 'documents',
			render: record => {
				return record?.documents?.length ? (
					<Popover
						overlay={
							<Padded>
								<AttachmentList
									disableEditing={true}
									deleteAttachment={async () => {}}
									currentAttachments={record.documents ?? []}
								/>
							</Padded>
						}
					>
						<IconButton iconName="FileDownload" alignCenter />
					</Popover>
				) : null;
			}
		},
		(editRule === ActivityEditRule.All || deleteRule === ActivityEditRule.All) && { title: 'Actions', isActionColumn: true }
	];

	return (
		<TableWrapper>
			<VerticalInset>
				<FlexRowSpaced>
					{allowAdd ? (
						<>
							{isMobile ? (
								<ActivityDrawerMobile
									useLinkedOrganisationData={useLinkedOrganisationData}
									defaultStatus={defaultStatus}
									activityLabel={activityLabel}
								/>
							) : (
								<ActivityDrawerDesktop
									useLinkedOrganisationData={useLinkedOrganisationData}
									defaultStatus={defaultStatus}
									activityLabel={activityLabel}
								/>
							)}
						</>
					) : null}
					<ExportButton useLinkedOrganisationData={useLinkedOrganisationData} activityLabel={activityLabel} />
				</FlexRowSpaced>
			</VerticalInset>
			<ResponsiveTable<SwiftFoxGoalActivity>
				columns={columns}
				data={activityData?.results}
				loading={activityLoading}
				rowsExpandedOnSmallScreens={rowsExpandedOnSmallScreens}
				rowActionComponent={record => (
					<FlexRowSpaced>
						{editRule === ActivityEditRule.All ? (
							<ActivityDrawerDesktop
								record={record}
								useLinkedOrganisationData={useLinkedOrganisationData}
								defaultStatus={defaultStatus}
								activityLabel={activityLabel}
							/>
						) : null}
						{deleteRule === ActivityEditRule.All ? <DeleteButtonDesktop id={record.id} activityLabel={activityLabel} /> : null}
					</FlexRowSpaced>
				)}
				mobileRowActionComponent={record => (
					<FlexRowSpaced>
						{editRule === ActivityEditRule.All ? (
							<ActivityDrawerMobile
								record={record}
								useLinkedOrganisationData={useLinkedOrganisationData}
								defaultStatus={defaultStatus}
								activityLabel={activityLabel}
							/>
						) : null}
						{deleteRule === ActivityEditRule.All ? <DeleteButtonMobile id={record.id} activityLabel={activityLabel} /> : null}
					</FlexRowSpaced>
				)}
				paginationProps={
					activityData?.total
						? {
								page,
								onPageChange: page => setPage(page),
								total: activityData.total,
								itemsPerPage: PAGE_SIZE
						  }
						: undefined
				}
			/>
		</TableWrapper>
	);
}

function ExportButton({ useLinkedOrganisationData, activityLabel }: { useLinkedOrganisationData: boolean; activityLabel: string }) {
	const { contentId, requestToken } = useContentContext();
	const [loading, setLoading] = useState<boolean>(false);
	const [error, setError] = useState<string>('');
	const [data, setData] = useState<Record<string, string>[]>(undefined);

	if (error) return <MuiAlert severity="error">{error}</MuiAlert>;

	if (!data) {
		return (
			<Button
				startIconName="Download"
				disabled={loading}
				onClick={async () => {
					setLoading(true);
					setError('');
					let url = `/Umbraco/Api/Membership/ExportGoalsActivities?contentId=${contentId}`;
					if (useLinkedOrganisationData) {
						url += `&useLinkedOrganisationData=${useLinkedOrganisationData}`;
					}

					const res = await fetch(url, {
						method: 'GET',
						headers: {
							Accept: 'application/json',
							'Content-Type': 'application/json',
							RequestVerificationToken: requestToken
						}
					});
					if (res.ok) {
						var result = await res.json();
						if (result.success === true) {
							const finalData: Record<string, string>[] = result.result.map(f => {
								const obj: Record<string, string> = {};
								f.columns.forEach(c => {
									const encoded = csvEncode(c.column);
									if (encoded) {
										obj[encoded] = csvEncode(c.value ?? '') ?? '';
									}
								});

								return obj;
							});
							if (finalData) setData(finalData);
						} else {
							setError(result.message);
						}
					} else {
						const error = await res.text();
						setError(error);
					}
					setLoading(false);
				}}
				variant="primary"
			>
				Export
			</Button>
		);
	}

	return (
		<FlexRowSpaced>
			<Button variant="tertiary" onClick={() => setData(undefined)}>
				Cancel
			</Button>
			<Button startIconName="Download" variant="primary" title="Download File" onClick={() => setData(undefined)}>
				<ButtonLinkWrapper>
					<CSVLink
						data={data ?? []}
						filename={`${activityLabel.toLowerCase()} - ${renderDate(new Date())}.csv`}
						enclosingCharacter={`"`}
					>
						Download
					</CSVLink>
				</ButtonLinkWrapper>
			</Button>
		</FlexRowSpaced>
	);
}
